import React, { useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import { axiosInstance, UrlUtils } from '@share/utils';
import { Routes, Urls } from '@share/constants';
import { Loading } from '@share/components';
import { ICondoWeeksFilters, LoadingSise } from '@common-types';
import { APP_SELECTOR } from '@constants';

import { CondoWeeksCard } from '../condo-week-card';
import { CondoWeeksEmpty } from '../condo-weeks-empty';

import CondoWeeks from '@assets/images/condo-weeks.jpg';

import { CondoWeeksFilters } from '../condo-week-filters';
import { INITIAL_FITLERS, ProcessFilterParams } from './utils';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';

import './style.scss';

const QUERY_PARAM = 'queryHash';
const REDIRECT_QUERY_PARAM = 'redirect';
const PAGE = 10;
const debounceTime = 300;
const scrollPixels = 450;

interface IProps extends RouteComponentProps {}

function CondoWeeksWrapperComponent(props: IProps) {

  const [loading, setLoading ] = useState(false);
  const [results, setResults ] = useState([]);
  const [filters, setFilters ] = useState({ ...INITIAL_FITLERS });
  const [currentPage, setCurrentPage ] = useState(1);
  const [totalOrderCount, setTotalOrderCount ] = useState(0);

  const currentPageRef = useRef(0);
  const totalOrderCountRef = useRef(0);
  const resultsRef = useRef([]);
  const filtersRef = useRef(filters);

  const root: HTMLDivElement = document.querySelector(APP_SELECTOR);

  const queryHash = useMemo(() => UrlUtils.getSearchParam(QUERY_PARAM), []);
  const redirect = useMemo(() => UrlUtils.getSearchParam(REDIRECT_QUERY_PARAM), []);

  useEffect(() => {
    
    if (isEmpty(queryHash)) {
      props.history.push(Routes.NotAuthorized);
    }

    setLoading(true);
    setResults([]);
    setCurrentPage(1);

    handleCall(filters);

    window.addEventListener('scroll', onScroll); 

    return () => {
      window.removeEventListener('scroll', onScroll);
    }
  }, []);

  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);
  useEffect(() => { resultsRef.current = results; }, [results]);
  useEffect(() => { totalOrderCountRef.current = totalOrderCount; }, [totalOrderCount]);
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  const onScroll = debounce(() => {
    const rootHeight = root.scrollHeight ? root.scrollHeight : root.offsetHeight;
    if (root && window.innerHeight + window.scrollY + scrollPixels >= rootHeight) {
      if (resultsRef?.current?.length < totalOrderCountRef?.current) {
        const newPage = currentPageRef.current + 1;

        setCurrentPage(newPage);

        handleCall(filtersRef.current, newPage);
      }
    }
  }, debounceTime);

  const handleFilterChange = (filters: ICondoWeeksFilters) => {
    handleCall(filters);
  }

  const handleClearFilter = () => {
    setResults([]);
    setCurrentPage(1);

    handleCall({ ...INITIAL_FITLERS });
  }

  const handleCall = (filters?: ICondoWeeksFilters, currentPage = 1) => {
    setLoading(true);

    const filterParams = ProcessFilterParams(filters);

    axiosInstance.get(`${Urls.MarketingWeeks}?page=${currentPage}&results=${PAGE}&${QUERY_PARAM}=${queryHash}${filterParams.params}`)
      .then((resp) => {
        const results = resp.data?.items?.length ? resp.data.items : [];

        setResults(results);
        setTotalOrderCount(resp.data?.totalOrderCount);
        setFilters({ ...filters, applied: filterParams.filterApplied });
      }).catch((err) => {
        console.error(err);
        props.history.push(Routes.BasicError);

        setResults([]);
        setCurrentPage(1);    
      }).finally(() => {
        setLoading(false);
      });
  }

  const filtersApplied = filters?.applied;

  return (
    <div className="condo-weeks-page">
      <div className="py-2 container-fluid dark-overlay" style={{ backgroundImage: `url(${CondoWeeks})`, backgroundPosition: 'center left', backgroundSize: 'cover' }}>
        <div className="row">
          <div className="col-md-6 mx-auto text-center pt-6 pb-4">
            <h1 className="text-white">Search Our Weeks Inventory</h1>
            <p className="lead text-white mb-0">We offer the biggest vacation value for our Members!</p>
          </div>
        </div>
      </div>

      <div className="condo-weeks-page__content">
        <div className="container-fluid">
          {(loading) ? (
            <div className="loading-container">
              <Loading size={LoadingSise.Medium} />
            </div>) : null}

          {!loading ? (
            <>
              <div className="row mb-6 mt-4">
                <div className="col-12 text-center">
                  <h3 className="mb-0" style={{ paddingBottom: '10px' }}>Weekly Condo Specials</h3>
                  <p>A listing of unbeatable discount prices on our Condo inventory</p>
                </div>
              </div>

              {(results?.length || filtersApplied) ? (
                <div className="row">
                  <div className="col-lg-12" style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px', fontWeight: 'bold' }}>
                    <CondoWeeksFilters filters={filters} onFiltersChange={handleFilterChange} />
                  </div>

                  {filtersApplied ? (
                    <div className="col-lg-12" style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px', fontWeight: 'bold' }}>
                      <label style={{ cursor: 'pointer', color: 'red' }} onClick={handleClearFilter}>Clear filters <FontAwesomeIcon icon={faTimesCircle} /> </label>
                    </div>) : null}
                </div>) : null}

              <div className="row">
                {(results?.length || filtersApplied) ? (
                  <div className="col-lg-12" style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px', fontWeight: 'bold' }}>
                    Displaying {results?.length} of {totalOrderCount} condos
                  </div>) : null}

                {results?.map((c, i) => (
                  <div className="col-lg-6" key={`index_card_${i}`} style={{ display: 'flex', justifyContent: 'center' }}>
                    <CondoWeeksCard condo={c} redirect={redirect} />
                  </div>))}

                {!results?.length ? (
                  <CondoWeeksEmpty />) : null}
              </div>

            </>) : null}
        </div>
      </div>
    </div>
  );
}

const ApplicationComponentWithRouter = withRouter(CondoWeeksWrapperComponent);

export const CondoWeeksWrapper = connect()(ApplicationComponentWithRouter);
